import React, { useState, useMemo } from 'react';
import { Eye, CheckCircle, AlertCircle, RotateCw, X, Power, ChevronRight, ChevronDown } from 'lucide-react';
import SupplierLogo from '../SupplierLogo';

const StatusBadge = ({ status }) => {
    const statusConfig = {
        connected: { text: 'Connecté', color: 'green', icon: <CheckCircle size={12} /> },
        not_connected: { text: 'Non connecté', color: 'gray', icon: <Power size={12} /> },
        token_expired: { text: 'Expiré', color: 'yellow', icon: <AlertCircle size={12} /> },
        error: { text: 'Erreur', color: 'red', icon: <AlertCircle size={12} /> },
        pending: { text: 'En cours', color: 'blue', icon: <RotateCw size={12} className="animate-spin" /> },
    };

    const config = statusConfig[status] || statusConfig.not_connected;
    const colorClasses = {
        green: 'bg-green-100 text-green-800',
        gray: 'bg-gray-100 text-gray-800',
        yellow: 'bg-yellow-100 text-yellow-800',
        red: 'bg-red-100 text-red-800',
        blue: 'bg-blue-100 text-blue-800',
    };

    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${colorClasses[config.color]}`}>
            {config.icon}
            {config.text}
        </span>
    );
};

const PCETable = ({ 
    pces, 
    onView,
    filters = {}, 
    onFilterChange = () => {}
}) => {

    const formatDateFR = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) throw new Error("Date invalide");
            return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
        } catch (e) {
            return 'Date invalide';
        }
    };

    const hasActiveFilters = Object.values(filters).some(value => value && value.trim() !== '');

    // Regroupement accordéon par entreprise. Mono-PCE → ligne classique (pas
    // d'accordéon). Multi-PCE → header de groupe avec chevron + sub-rows
    // dépliables.
    const [expandedGroups, setExpandedGroups] = useState(() => new Set());

    const groupedPCEs = useMemo(() => {
        const map = new Map();
        for (const pce of (pces || [])) {
            const key = pce.account_id || pce.company_name || `__solo__${pce.id}`;
            if (!map.has(key)) {
                map.set(key, {
                    key,
                    companyName: pce.company_name || 'Sans entreprise',
                    pces: [],
                });
            }
            map.get(key).pces.push(pce);
        }
        return Array.from(map.values());
    }, [pces]);

    const toggleGroup = (key) => {
        setExpandedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key); else next.add(key);
            return next;
        });
    };

    return (
        <div className="overflow-auto max-h-[500px] border rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr>
                        <th scope="col" className="px-4 py-2">
                            <div className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                                Entreprise
                            </div>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Filtrer..." 
                                    value={filters.company_name || ''} 
                                    onChange={(e) => onFilterChange('company_name', e.target.value)} 
                                    className="w-full text-xs p-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                {filters.company_name && (
                                    <button
                                        onClick={() => onFilterChange('company_name', '')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </th>

                        <th scope="col" className="px-4 py-2">
                            <div className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                                Site / PCE
                            </div>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Filtrer..." 
                                    value={filters.site_name || ''} 
                                    onChange={(e) => onFilterChange('site_name', e.target.value)} 
                                    className="w-full text-xs p-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                {filters.site_name && (
                                    <button
                                        onClick={() => onFilterChange('site_name', '')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </th>

                        <th scope="col" className="px-4 py-2">
                            <div className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                                Fournisseur
                            </div>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Filtrer..." 
                                    value={filters.supplier_name || ''} 
                                    onChange={(e) => onFilterChange('supplier_name', e.target.value)} 
                                    className="w-full text-xs p-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                {filters.supplier_name && (
                                    <button
                                        onClick={() => onFilterChange('supplier_name', '')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </th>

                        <th scope="col" className="px-4 py-2">
                            <div className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                                Statut
                            </div>
                        </th>

                        <th scope="col" className="px-4 py-2">
                            <div className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                                Conso / Tarif
                            </div>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Filtrer..." 
                                    value={filters.car_kwh || ''} 
                                    onChange={(e) => onFilterChange('car_kwh', e.target.value)} 
                                    className="w-full text-xs p-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                {filters.car_kwh && (
                                    <button
                                        onClick={() => onFilterChange('car_kwh', '')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </th>

                        <th scope="col" className="px-4 py-2">
                            <div className="text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2">
                                Échéance Contrat
                            </div>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    placeholder="Filtrer..." 
                                    value={filters.contract_end_date || ''} 
                                    onChange={(e) => onFilterChange('contract_end_date', e.target.value)} 
                                    className="w-full text-xs p-1.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                                />
                                {filters.contract_end_date && (
                                    <button
                                        onClick={() => onFilterChange('contract_end_date', '')}
                                        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                        </th>

                        <th scope="col" className="relative px-4 py-2">
                            <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Détails</span>
                        </th>
                    </tr>
                </thead>

                <tbody className="bg-white divide-y divide-gray-200">
                    {groupedPCEs.length > 0 ? (
                        groupedPCEs.flatMap((group) => {
                            // Mono-PCE : ligne classique (pas d'accordéon)
                            if (group.pces.length === 1) {
                                const pce = group.pces[0];
                                return [(
                                    <tr key={pce.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-4 py-3 whitespace-nowrap text-xs font-semibold text-gray-800">{pce.company_name || 'N/A'}</td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="text-xs font-medium text-gray-900">{pce.site_name}</div>
                                            <div className="text-xs text-gray-500 font-mono">{pce.pce_number}</div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <SupplierLogo supplierName={pce.supplier_name} logoUrl={pce.supplier_logo_url} size="sm" />
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={pce.connection_status} /></td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                                            {pce.car_kwh ? <div>{new Intl.NumberFormat('fr-FR').format(pce.car_kwh)} kWh/an</div> : <div>N/A</div>}
                                            {pce.tarif_gaz_code && <div className="text-xs text-gray-500 font-semibold">{pce.tarif_gaz_code}</div>}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">{formatDateFR(pce.contract_end_date)}</td>
                                        <td className="px-4 py-3 whitespace-nowrap text-right text-xs font-medium">
                                            <button onClick={() => onView(pce)} className="text-gray-400 hover:text-green-600 transition-colors" title="Voir les détails">
                                                <Eye size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                )];
                            }

                            // Multi-PCE : header de groupe + accordéon
                            const isOpen = expandedGroups.has(group.key);
                            const headerRow = (
                                <tr
                                    key={`grp-${group.key}`}
                                    onClick={() => toggleGroup(group.key)}
                                    className="bg-slate-100 hover:bg-slate-200 cursor-pointer border-b border-slate-300 select-none"
                                >
                                    <td colSpan={7} className="px-4 py-2">
                                        <div className="flex items-center gap-2">
                                            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                            <span className="text-xs font-semibold text-gray-800">{group.companyName}</span>
                                            <span className="text-[10px] text-gray-500">{group.pces.length} PCEs</span>
                                        </div>
                                    </td>
                                </tr>
                            );
                            if (!isOpen) return [headerRow];
                            const childRows = group.pces.map((pce) => (
                                <tr key={pce.id} className="hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 whitespace-nowrap text-[10px] text-gray-400 pl-10">↳</td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <div className="text-xs font-medium text-gray-900">{pce.site_name}</div>
                                        <div className="text-xs text-gray-500 font-mono">{pce.pce_number}</div>
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap">
                                        <SupplierLogo supplierName={pce.supplier_name} logoUrl={pce.supplier_logo_url} size="sm" />
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap"><StatusBadge status={pce.connection_status} /></td>
                                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-900">
                                        {pce.car_kwh ? <div>{new Intl.NumberFormat('fr-FR').format(pce.car_kwh)} kWh/an</div> : <div>N/A</div>}
                                        {pce.tarif_gaz_code && <div className="text-xs text-gray-500 font-semibold">{pce.tarif_gaz_code}</div>}
                                    </td>
                                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-700">{formatDateFR(pce.contract_end_date)}</td>
                                    <td className="px-4 py-3 whitespace-nowrap text-right text-xs font-medium">
                                        <button onClick={() => onView(pce)} className="text-gray-400 hover:text-green-600 transition-colors" title="Voir les détails">
                                            <Eye size={16} />
                                        </button>
                                    </td>
                                </tr>
                            ));
                            return [headerRow, ...childRows];
                        })
                    ) : (
                        <tr>
                            <td colSpan="7" className="px-4 py-8">
                                <div className="text-center">
                                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                                        <AlertCircle className="h-8 w-8 text-gray-400" />
                                    </div>
                                    <h3 className="text-xs font-semibold text-gray-900 mb-2">
                                        Aucun résultat trouvé
                                    </h3>
                                    <p className="text-xs text-gray-600">
                                        {hasActiveFilters 
                                            ? "Aucun PCE ne correspond à vos critères de filtrage."
                                            : "Aucun PCE n'a été trouvé."}
                                    </p>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export default PCETable;
